import * as d3 from 'd3';
import { colors, DURATION } from '../utils';
import './d3-rowchart.css';

export default function () {
  const margin = { top: 10, right: 40, bottom: 60, left: 80 };

  let width = 400;
  let height = 300;
  let svg;
  let t;
  let _data;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let color;
  let selected;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      _data = data;

      const duration = isResize ? 0 : DURATION;
      t = d3.transition().duration(duration);

      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();
        createLabel();

        g.append('g')
          .attr('class', 'row-chart__bars')
          .append('path');
      }

      svg.attr('width', width)
        .attr('height', height);

      drawAxes();
      drawLabel();
      drawBars();
    });
  }

  function createScales() {
    x = d3.scaleLinear().rangeRound([0, chartWidth]);
    y = d3.scaleBand().range([0, chartHeight]).padding(0.1);
    color = d3.scaleOrdinal(d3.schemeCategory10);

    const xMin = d3.min(_data.map(function (d) { return Math.min(0, d.value); }));
    const xMax = d3.max(_data.map(function (d) { return Math.max(0, d.value); }));

    x.domain([xMin, xMax]).nice();
    y.domain(_data.map(function (d) { return d.id; }));
    color.domain(_data.map(function (d) { return d.id; }));
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'row-chart__axis row-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'row-chart__axis row-chart__axis--y')
  }

  function createLabel() {
    svg.append('text')
      .attr('class', 'row-chart__label')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('(£Mpa)');
  }

  function drawAxes() {
    const xAxis = svg.select('.row-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .transition(t)
      .call(d3.axisBottom(x).tickFormat(function (d) { return d3.format(',.0f')(d / 1e6); }).tickSize(-chartHeight));

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    xAxis.selectAll('.tick text')
      .attr('dx', '-0.8em')
      .attr('dy', '.35em');

    const yAxis = svg.select('.row-chart__axis--y');

    yAxis.call(d3.axisLeft(y).tickSize(0))
      .select('.domain').remove();
  }

  function drawLabel() {
    svg.select('.row-chart__label')
      .attr('x', width / 2)
      .attr('y', height - 20);
  }

  function drawBars() {
    const bars = svg.select('.row-chart__bars')
      .selectAll('.row-chart__bar')
      .data(_data);

    const barsEnter = bars.enter()
      .append('g')
      .attr('class', 'row-chart__bar')
      .on('click', function (d) {
        dispatch.call('selected', chart, 'asset', d.id);
      });

    barsEnter.append('rect')
      .attr('class', 'row-chart__bar--rect')
      .attr('stroke-width', 2)
      .attr('x', function (d) { return x(0); })
      .attr('y', function (d) { return y(d.id); })
      .merge(bars.select('rect'))
      .transition(t)
      .attr('fill', function (d) { return d.id === selected ? colors.red : colors.lightgray; })
      .attr('x', function (d) { return x(Math.min(0, d.value)); })
      .attr('y', function (d) { return y(d.id); })
      .attr('width', function (d) { return Math.abs(x(d.value) - x(0)); })
      .attr('height', y.bandwidth());

    barsEnter.append('text')
      .attr('class', 'row-chart__bar--label')
      .attr('x', function (d) { return x(d.value); })
      .attr('y', function (d) { return y(d.id) + y.bandwidth() / 2; })
      .attr('dx', function (d) {
        return d.value >= 0 ? (Math.abs(x(d.value) - x(0)) > 40 ? '-4px' : '4px') : (Math.abs(x(d.value) - x(0)) > 40 ? '4px' : '-4px');
      })
      .attr('text-anchor', function (d) {
        return d.value >= 0 ? (Math.abs(x(d.value) - x(0)) > 40 ? 'end' : 'start') : (Math.abs(x(d.value) - x(0)) > 40 ? 'start' : 'end');
      })
      .attr('alignment-baseline', 'middle')
      .attr('fill', function (d) { return d.id === selected ? 'white' : 'black'; })
      .attr('font-weight', 'bold')
      .text(function (d) { return d3.formatPrefix(',.0', 1e6)(d.value); })
      .merge(bars.select('text'))
      .transition(t)
      .attr('x', function (d) { return x(d.value); })
      .attr('y', function (d) { return y(d.id) + y.bandwidth() / 2; })
      .attr('dx', function (d) {
        return d.value >= 0 ? (Math.abs(x(d.value) - x(0)) > 40 ? '-4px' : '4px') : (Math.abs(x(d.value) - x(0)) > 40 ? '4px' : '-4px');
      })
      .attr('text-anchor', function (d) {
        return d.value >= 0 ? (Math.abs(x(d.value) - x(0)) > 40 ? 'end' : 'start') : (Math.abs(x(d.value) - x(0)) > 40 ? 'start' : 'end');
      })
      .attr('fill', function (d) { return d.id === selected ? (Math.abs(x(d.value) - x(0)) > 40 ? 'white' : 'black') : 'black'; })
      .text(function (d) { return d3.formatPrefix(',.0', 1e6)(d.value); });
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-rowchart.js